/** @format */

import React, { useEffect, useMemo, useState } from 'react';
import ModalImageGallery from './image-modal';
import Translate from '@components/translation/translate-text-wrapper';
import { useSafeTranslate } from '@hooks/use-safe-translate';
import axios from 'axios';
import Loader from '@components/loader';
import { startLazyLoad } from '@services/lazy-load';

type GalleryData = {
  image_groups: GalleryGroup[];
  images: GalleryImage[];
};

type GalleryGroup = {
  group_id: string;
  group_name: string;
};

type GalleryImage = {
  widen_image_id: string;
  group_name: string;
  image_group_id: string;
  title: string;
  nid: string;
  alt_tag: string;
};

/**
 * Image Gallery.
 *
 * @returns
 */
const ImageGallery = (): JSX.Element | null => {
  const [galleryData, setGalleryData] = useState<GalleryData | null>(null);
  const [searchWord, setSearchWord] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const showAll = useSafeTranslate('Show all', true);

  /**
   * Fetch gallery data.
   */
  useMemo(() => {
    axios
      .get('/rest-api/varian-image-gallery-data?_format=json')
      .then((response) => {
        setGalleryData(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  /**
   * Lazy load images.
   */
  useEffect(() => {
    setTimeout(() => {
      startLazyLoad();
    }, 50);
  }, [selectedCategory, searchWord]);

  /**
   * Render.
   */
  if (galleryData) {
    return (
      <>
        <div className="image-gallery medium-vertical-margin">
          <h3 className="font-size-md">
            <Translate>Select category or search by name</Translate>:
          </h3>

          <div className="image-gallery-search-container small-vertical-margin">
            {/* Gallery search */}
            <input
              type="search"
              onChange={(e) => setSearchWord(e.target.value)}
              value={searchWord}
            />

            {/* Gallery dropdown */}
            <select onChange={(e) => setSelectedCategory(e.target.value)}>
              <option value="all">-- {showAll} --</option>
              {galleryData?.image_groups?.map((category: GalleryGroup) => {
                const { group_id: id, group_name: name } = category;
                return (
                  <Translate key={id} htmlTag="option" optionValue={id}>
                    {name}
                  </Translate>
                );
              })}
            </select>
          </div>

          {/* Gallery */}
          {galleryData?.images?.length > 0 && (
            <div className="grid-4-columns img-gallery-4-columns">
              {galleryData?.images.map((galleryImage: GalleryImage) => {
                const {
                  title: imageTitle,
                  widen_image_id: image,
                  image_group_id: groupId,
                  alt_tag: altTag,
                  nid,
                } = galleryImage;
                if (
                  selectedCategory === 'all' ||
                  groupId === selectedCategory
                ) {
                  if (
                    searchWord.length < 3 ||
                    (searchWord.length >= 3 &&
                      imageTitle
                        .toLowerCase()
                        .indexOf(searchWord.toLowerCase()) > -1)
                  ) {
                    return (
                      <ModalImageGallery
                        key={nid}
                        title={imageTitle}
                        image={image}
                        altTag={altTag}
                      />
                    );
                  }
                }
              })}
            </div>
          )}
        </div>
      </>
    );
  }
  return <Loader />;
};

export default ImageGallery;
